import difference from 'lodash/difference';

import type { Store, UserMenuData, UserMenuDataShapes } from '../../types';
import { getMenuIdsByVisible } from '../get-menu-ids-by-visible';
import { repairItems } from '../repair-items';

// Build common shapes out of user menu data.
// We do it here, once, so components don't perform the same filtering and mapping in more than one place.
export const buildUserMenuDataShapes = (
	defaultItemMenuIds: string[],
	defaultRawShortcutMenuIds: string[],
	rawUserMenuData: UserMenuData | null | undefined,
	setUserMenuData: Store['setUserMenuData'],
): UserMenuDataShapes => {
	// Remove any menu ids that overlap with shortcuts
	const distinctDefaultItemMenuIds = difference(defaultItemMenuIds, defaultRawShortcutMenuIds);

	// Add missing items and remove obsolete or duplicate items
	const itemUserMenuData = repair(rawUserMenuData, distinctDefaultItemMenuIds);
	// Add missing shortcuts and remove obsolete or duplicate shortcuts
	const rawShortcutUserMenuData = repair(rawUserMenuData, defaultRawShortcutMenuIds);
	// This repaired user menu data should always be used instead of the raw user menu data
	const userMenuData = itemUserMenuData.concat(rawShortcutUserMenuData);

	const [visibleItemMenuIds, hiddenItemMenuIds] = getMenuIdsByVisible(itemUserMenuData);

	const [visibleRawShortcutMenuIds, hiddenRawShortcutMenuIds] =
		getMenuIdsByVisible(rawShortcutUserMenuData);

	const visibleMenuIds = visibleItemMenuIds.concat(visibleRawShortcutMenuIds);
	const hiddenMenuIds = hiddenItemMenuIds.concat(hiddenRawShortcutMenuIds);

	return {
		hiddenItemMenuIds,
		hiddenMenuIds,
		hiddenRawShortcutMenuIds,
		itemUserMenuData,
		rawShortcutUserMenuData,
		setUserMenuData,
		userMenuData,
		visibleItemMenuIds,
		visibleMenuIds,
		visibleRawShortcutMenuIds,
	};
};

const repair = (rawUserMenuData: UserMenuData | null | undefined, defaultMenuIds: string[]) =>
	repairItems(rawUserMenuData ?? [], createDefaultItems(defaultMenuIds));

const createDefaultItems = (menuIds: string[]): UserMenuData =>
	menuIds.map((menuId) => ({ menuId, visible: true }));
